// Standalone "Alt-part search" page. Type any part number → resolve its NSN(s)
// via DLA FLIS and list every other CAGE + reference number to re-query on
// PartsBase. Same resolver as the per-line-item finder, but not tied to an RFQ.

import { useState, type FormEvent } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { Search, Copy, Check } from 'lucide-react';
import { AppShell } from '@/components/app-shell';
import { api, reportAltPartCopy, type AltPartsResponse } from '@/lib/api';
import {
  AltPartRelationshipLabel,
  AltPartCaveat,
  AltPartSourceLine,
} from '@/components/alt-part-provenance';

export function AltPartSearchPage() {
  const { getToken } = useAuth();
  const [query, setQuery] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [copied, setCopied] = useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['alt-part-search', submitted],
    enabled: submitted.length >= 2,
    queryFn: () =>
      api<AltPartsResponse>(`/api/alt-parts?q=${encodeURIComponent(submitted)}`, {
        tokenGetter: () => getToken(),
      }),
    staleTime: 5 * 60 * 1000,
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(query.trim());
  };

  const copyAll = async () => {
    if (!data?.altParts.length) return;
    const text = data.altParts.map((a) => a.refNumber).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      void reportAltPartCopy(submitted, 'search_page', () => getToken());
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Fires here too: navigator.clipboard is unavailable in a non-secure
      // context and on older Safari, and only counting the happy path would
      // bias the usage metric against exactly those sessions.
      void reportAltPartCopy(submitted, 'search_page', () => getToken());
      window.prompt('Copy the cross-referenced P/Ns:', text);
    }
  };

  return (
    <AppShell
      title="Alt-part search"
      subtitle="Find part numbers cross-referenced to the same NSN via DLA FLIS"
    >
      <div className="h-full overflow-auto px-6 py-6 space-y-4 max-w-4xl">
        <form onSubmit={onSubmit} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground-muted pointer-events-none" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Enter a part number — e.g. MS21042-3"
              className="w-full pl-9 pr-3 py-2 text-sm bg-background border border-border rounded-md font-mono"
              autoFocus
            />
          </div>
          <button
            type="submit"
            disabled={query.trim().length < 2}
            className="px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md disabled:opacity-50"
          >
            Search
          </button>
        </form>

        {!submitted && (
          <div className="card p-8 text-sm text-foreground-muted text-center">
            Enter a part number to find its NSN and every other part number catalogued against it (CAGE + part number).
            Best results on standardized P/Ns (MS, NAS, AN, AS…). Punctuation/spacing is ignored.
          </div>
        )}

        {isLoading && submitted && (
          <div className="card p-8 text-sm text-foreground-muted text-center">Searching DLA FLIS…</div>
        )}

        {isError && (
          <div className="card p-4 text-sm text-destructive">
            {(error as { message?: string })?.message ?? 'Lookup unavailable — try again later.'}
          </div>
        )}

        {data?.lookupStatus === 'not_found' && (
          <div className="card p-6 text-sm text-foreground-muted text-center">
            No NSN match in DLA FLIS for <span className="font-mono text-foreground">{data.originalPartNumber}</span>.
            <div className="mt-1 text-xs">Common for proprietary/commercial OEM part numbers — they're not federally cataloged.</div>
          </div>
        )}

        {data?.lookupStatus === 'found' && (
          <div className="space-y-3">
            <div>
              <AltPartRelationshipLabel />
            </div>
            <div className="text-sm text-foreground-muted">
              <span className="font-mono text-foreground">{data.originalPartNumber}</span> →{' '}
              {data.nsns.map((n) => (
                <span key={n.nsn} className="font-mono text-foreground mr-2">
                  {n.nsn}{n.itemName ? ` (${n.itemName})` : ''}
                </span>
              ))}
            </div>

            {data.altParts.length === 0 ? (
              <div className="card p-6 text-sm text-foreground-muted text-center">
                This P/N is the only reference number on its NSN — nothing cross-referenced.
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-foreground-muted">
                    {data.altParts.length} cross-referenced P/N{data.altParts.length === 1 ? '' : 's'}
                    {data.truncated ? ' (showing first 1000)' : ''} — re-query these on PartsBase:
                  </span>
                  <button onClick={copyAll} className="flex items-center gap-1 text-primary hover:underline font-medium">
                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied ? 'Copied' : 'Copy all P/Ns'}
                  </button>
                </div>
                <div className="card overflow-x-auto">
                  <table className="w-full min-w-[720px] text-left text-sm">
                    <thead className="bg-surface-muted/60 text-foreground-muted">
                      <tr>
                        <th className="px-3 py-2 font-medium">CAGE</th>
                        <th className="px-3 py-2 font-medium">Cross-Referenced P/N</th>
                        <th className="px-3 py-2 font-medium">Item Name</th>
                        <th className="px-3 py-2 font-medium">Company</th>
                        <th className="px-3 py-2 font-medium">NSN</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.altParts.map((a, i) => (
                        <tr key={`${a.cageCode}-${a.refNumber}-${i}`} className="border-t border-border">
                          <td className="px-3 py-2 font-mono">{a.cageCode}</td>
                          <td className="px-3 py-2 font-mono">{a.refNumber}</td>
                          <td className="px-3 py-2 text-foreground-muted">{a.itemName ?? '—'}</td>
                          <td className="px-3 py-2 text-foreground-muted">{a.companyName ?? '—'}</td>
                          <td className="px-3 py-2 font-mono text-foreground-muted">{a.nsn}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            <AltPartCaveat className="pt-2 border-t border-border" />
            <AltPartSourceLine dataset={data.dataset} className="pt-1" />
          </div>
        )}
      </div>
    </AppShell>
  );
}
